'use client';

import * as React from 'react';
import * as SwitchPrimitive from '@radix-ui/react-switch';
import { cn } from '@/lib/ui/utils';

type SwitchProps = React.ComponentPropsWithoutRef<typeof SwitchPrimitive.Root>;

const Switch = React.forwardRef<
  React.ElementRef<typeof SwitchPrimitive.Root>,
  SwitchProps
>(
  (
    { checked, className, defaultChecked, onCheckedChange, ...props },
    ref
  ) => {
    const isControlled = typeof checked === 'boolean';
    const [internalChecked, setInternalChecked] = React.useState(
      Boolean(defaultChecked)
    );
    const currentChecked = isControlled ? checked : internalChecked;

    const handleCheckedChange = (nextChecked: boolean) => {
      if (!isControlled) {
        setInternalChecked(nextChecked);
      }

      onCheckedChange?.(nextChecked);
    };

  return (
    <SwitchPrimitive.Root
      ref={ref}
      checked={currentChecked}
      data-slot="switch"
      className={cn(
        'peer relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-pill border border-transparent shadow-sm transition-colors outline-none',
        currentChecked ? 'bg-primary' : 'bg-input dark:bg-input/80',
        'focus-visible:ring-2 focus-visible:ring-ring/50',
        'disabled:cursor-not-allowed disabled:opacity-50',
        className
      )}
      onCheckedChange={handleCheckedChange}
      {...props}
    >
      <SwitchPrimitive.Thumb
        className={cn(
          'pointer-events-none absolute left-0.5 top-0.5 block size-5 rounded-pill bg-background shadow-sm transition-transform',
          currentChecked
            ? 'dark:bg-primary-foreground'
            : 'dark:bg-foreground'
        )}
        style={{
          transform: currentChecked ? 'translateX(20px)' : 'translateX(0px)',
        }}
      />
    </SwitchPrimitive.Root>
  );
  }
);

Switch.displayName = SwitchPrimitive.Root.displayName;

export { Switch };
